import { useCallback, useState } from 'react';
import { Info, MoreVertical, Trash2 } from 'lucide-react';
import { Menu, Tooltip } from '@mui/material';
import { tokens } from '@/styles/tokens';
import * as S from './styles';
import { ACTIONS_CLASS, REVEAL_CLASS } from './styles';
import type {
  DocumentationDeleteMenuProps,
  DocumentationHoverRevealProps,
  DocumentationLegendCounterProps,
  DocumentationLegendGlyphProps,
  DocumentationLegendListProps,
  DocumentationLegendRowProps,
  DocumentationPanelProps,
  DocumentationSectionProps,
} from './types';

/**
 * Reusable documentation surface shared by the PDF editor's drawer and the
 * data-analysis view side panel: the sectioned layout, the legend list and its empty
 * state, and ONE legend entry (`DocumentationLegendEntry`) that both legends draw. The
 * text fields are `GhostInput`s from `@shared/ui/ghost-input`. What the surfaces still
 * pass in is their editor — the collaborative Tiptap editor, which `shared` cannot
 * import — and their data: the two legends key their entries differently (the PDF by
 * tool and colour, data analysis by symbol).
 *
 * Compose it:
 *   <DocumentationPanel>
 *     <DocumentationSection label={t('work_performed')} grow>{editor}</DocumentationSection>
 *     <DocumentationSection label={t('legend')} description={…} divider>
 *       <DocumentationLegendList isEmpty={…} emptyLabel={…}>
 *         {rows.map(r => <DocumentationLegendRow leading={…} actions={…}>{label}</…>)}
 *       </DocumentationLegendList>
 *     </DocumentationSection>
 *   </DocumentationPanel>
 */
export function DocumentationPanel({
  children,
  spacing = 1.5,
  padding = 3,
  className,
  'data-testid': dataTestId,
}: DocumentationPanelProps) {
  return (
    <S.PanelRoot spacing={spacing} p={padding} className={className} data-testid={dataTestId}>
      {children}
    </S.PanelRoot>
  );
}

export function DocumentationSection({
  label,
  description,
  info,
  headerAction,
  divider,
  grow,
  children,
  className,
  'data-testid': dataTestId,
}: DocumentationSectionProps) {
  return (
    <>
      {divider && <S.SectionDivider />}
      <S.Section grow={grow} className={className} data-testid={dataTestId}>
        <S.SectionHeader>
          <S.SectionLabelGroup>
            <S.SectionLabel>{label}</S.SectionLabel>
            {info != null && (
              <Tooltip title={info} arrow>
                <Info
                  size={12}
                  color={tokens.grey[60]}
                  aria-label={typeof info === 'string' ? info : undefined}
                  data-testid='section-info'
                />
              </Tooltip>
            )}
          </S.SectionLabelGroup>
          {headerAction}
        </S.SectionHeader>
        {description != null && <S.SectionDescription>{description}</S.SectionDescription>}
        {children}
      </S.Section>
    </>
  );
}

export function DocumentationLegendList({
  emptyLabel,
  isEmpty,
  isLoading = false,
  loadingLabel,
  children,
  'data-testid': dataTestId,
}: DocumentationLegendListProps) {
  return (
    <S.LegendList data-testid={dataTestId}>
      {isLoading ? (
        <S.LoadingLegend data-testid='documentation-legend-loading'>{loadingLabel}</S.LoadingLegend>
      ) : isEmpty ? (
        <S.EmptyLegend>{emptyLabel}</S.EmptyLegend>
      ) : (
        children
      )}
    </S.LegendList>
  );
}

export function DocumentationLegendRow({
  leading,
  children,
  actions,
  highlighted = false,
  hideActionsWhileEditing = false,
  onClick,
  className,
  'data-testid': dataTestId,
}: DocumentationLegendRowProps) {
  return (
    <S.LegendRowRoot
      highlighted={highlighted}
      onClick={onClick}
      className={className}
      data-highlighted={highlighted ? 'true' : 'false'}
      data-testid={dataTestId}
      style={onClick ? { cursor: 'pointer' } : undefined}
    >
      <S.LegendLeading>{leading}</S.LegendLeading>
      <S.LegendBody hideActionsWhileEditing={hideActionsWhileEditing}>{children}</S.LegendBody>
      {actions != null && <S.LegendActions className={ACTIONS_CLASS}>{actions}</S.LegendActions>}
    </S.LegendRowRoot>
  );
}

/**
 * The mark-glyph slot for a legend row — interactive (button + hover fill) when
 * `onClick` is given, a static span otherwise. Shared so the editor and
 * data-analysis legends render the glyph chrome identically; each screen supplies
 * its own glyph (tickmark / note badge) as the child.
 */
export function DocumentationLegendGlyph({
  children,
  onClick,
  disabled,
  ariaLabel,
  title,
  className,
  'data-testid': dataTestId,
}: DocumentationLegendGlyphProps) {
  if (!onClick) {
    // A bare span may not carry a name: with one, the glyph is an image of the mark.
    return (
      <S.LegendGlyphStatic
        className={className}
        role={ariaLabel ? 'img' : undefined}
        aria-label={ariaLabel}
        data-testid={dataTestId}
      >
        {children}
      </S.LegendGlyphStatic>
    );
  }
  return (
    <S.LegendGlyphButton
      type='button'
      disabled={disabled}
      aria-label={ariaLabel}
      title={title}
      className={className}
      data-testid={dataTestId}
      onClick={onClick}
    >
      {children}
    </S.LegendGlyphButton>
  );
}

/** The usage-counter pill on a legend row (e.g. opens the "N places" popover). */
export function DocumentationLegendCounter({
  children,
  onClick,
  onMouseEnter,
  onMouseLeave,
  ariaLabel,
  active,
  'data-testid': dataTestId,
}: DocumentationLegendCounterProps) {
  if (!onClick && !onMouseEnter) {
    return (
      <S.LegendCounterStatic
        active={active}
        role={ariaLabel ? 'img' : undefined}
        aria-label={ariaLabel}
        data-testid={dataTestId}
      >
        {children}
      </S.LegendCounterStatic>
    );
  }
  return (
    <S.LegendCounter
      type='button'
      active={active}
      aria-label={ariaLabel}
      data-testid={dataTestId}
      onClick={onClick}
      onMouseEnter={onMouseEnter}
      onMouseLeave={onMouseLeave}
    >
      {children}
    </S.LegendCounter>
  );
}

/** Wrap any control that should fade in only while its row is hovered. */
export function DocumentationHoverReveal({
  children,
  forceVisible = false,
  className,
}: DocumentationHoverRevealProps) {
  return (
    <S.RevealBox
      forceVisible={forceVisible}
      className={[REVEAL_CLASS, className].filter(Boolean).join(' ')}
    >
      {children}
    </S.RevealBox>
  );
}

/** Kebab → Delete menu, hover-revealed and kept visible while open. */
export function DocumentationDeleteMenu({
  onDelete,
  disabled = false,
  deleteDisabled = false,
  deleteLabel,
  deleteIcon,
  deleteTooltip,
  triggerLabel,
  'data-testid': dataTestId,
}: DocumentationDeleteMenuProps) {
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const open = Boolean(anchorEl);

  const handleClose = useCallback(() => setAnchorEl(null), []);
  const handleDelete = useCallback(() => {
    // The item only looks disabled (see below), so the refusal is enforced here.
    if (deleteDisabled) return;
    handleClose();
    onDelete();
  }, [deleteDisabled, handleClose, onDelete]);

  /**
   * A refused item is `aria-disabled` and drawn disabled, but not MUI-`disabled`. MUI's
   * disabled MenuItem takes no pointer events, so its tooltip, the only place the reason
   * is written, could never open; wrapping it in a span to restore the hover put a
   * non-item inside `role="menu"`, which assistive technology cannot read as a menu.
   */
  const deleteItem = (
    <S.DeleteMenuItem
      onClick={handleDelete}
      refused={deleteDisabled}
      aria-disabled={deleteDisabled || undefined}
    >
      {deleteIcon ?? <Trash2 />}
      {deleteLabel}
    </S.DeleteMenuItem>
  );

  return (
    <DocumentationHoverReveal forceVisible={open}>
      <S.MenuButton
        aria-label={triggerLabel}
        onClick={(e) => {
          e.stopPropagation();
          setAnchorEl(e.currentTarget);
        }}
        disabled={disabled}
        data-testid={dataTestId}
      >
        <MoreVertical size={18} />
      </S.MenuButton>
      <Menu
        anchorEl={anchorEl}
        open={open}
        onClose={handleClose}
        onClick={(e) => e.stopPropagation()}
        transformOrigin={{ horizontal: 'right', vertical: 'top' }}
        anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
        slotProps={{
          paper: {
            sx: {
              minWidth: 140,
              mt: 0.5,
              boxShadow: '0px 4px 20px rgba(0, 0, 0, 0.1)',
              borderRadius: '8px',
            },
          },
        }}
      >
        {deleteTooltip != null ? (
          // `describeChild`: the reason describes the item; its name stays its label.
          <Tooltip title={deleteTooltip} placement='left' describeChild>
            {deleteItem}
          </Tooltip>
        ) : (
          deleteItem
        )}
      </Menu>
    </DocumentationHoverReveal>
  );
}

export { DocumentationLegendEmpty } from './DocumentationLegendEmpty';
export type { DocumentationLegendEmptyProps } from './DocumentationLegendEmpty';

export { DocumentationHintList } from './DocumentationHintList';
export type { DocumentationHintListProps } from './DocumentationHintList';

export type {
  DocumentationDeleteMenuProps,
  DocumentationHoverRevealProps,
  DocumentationLegendCounterProps,
  DocumentationLegendGlyphProps,
  DocumentationLegendListProps,
  DocumentationLegendRowProps,
  DocumentationPanelProps,
  DocumentationSectionProps,
} from './types';

export { DocumentationTabView } from './DocumentationTabView';
export type { DocumentationTabViewProps } from './DocumentationTabView';

export { DocumentationLegendEntry } from './DocumentationLegendEntry';
export { DocumentationLegendBlocks } from './DocumentationLegendBlocks';
export type {
  DocumentationLegendBlocksProps,
  DocumentationLegendEntryAction,
  DocumentationLegendEntryProps,
} from './types';
